// Abschnitt "Name" auf der Kontaktseite (ST-PTY-001, CM-PTY-002).
//
// PROGRESSIVE OFFENLEGUNG (5.2 Nr. 9): der Name steht im Kopf; geaendert wird
// er hier, zugeklappt unter "Name", ohne eigenen Bildschirm. Die Hauptaktion
// der Seite bleibt "Notiz festhalten"; "Namen speichern" ist die eine Aktion
// dieses Abschnitts und deshalb nicht primaer gesetzt.
//
// SICHERE FEHLERERHOLUNG (NFR-UX-005, CM-UX-004): ein Konflikt laedt still
// nach und zeigt den jetzt gueltigen Namen, statt die Eingabe zu wiederholen;
// eine abgewiesene Eingabe bleibt stehen und ist am Feld markiert. Der Abschnitt
// laedt nicht die ganze Seite neu: sie haengte ihn samt Bestaetigung und Fokus
// aus. Er reicht die neue Party nach oben (`onParty`).
//
// BARRIEREFREIHEIT (NFR-ACC-001): genau eine Statusregion; der Fokus geht nach
// jedem Befehl dorthin, in einem Effekt nach dem Rerender (WCAG 2.4.3).

import { useState, type FormEvent, type ReactElement } from 'react';
import { Body1, Button, Field, Input, makeStyles, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';

import type { PlainMessage } from '../../access.ts';
import { fetchParty, type PartyDetail } from '../../parties.ts';
import {
  PERSON_NAME_MESSAGES,
  personNameConfirmation,
  personNameValidationMessage,
  renamePerson,
  UNKNOWN_LOCK_VERSION_MESSAGE,
} from '../../person-name.ts';
import { useSectionCommands, meldungsIntent } from '../../shell/use-section-commands.ts';

const useStyles = makeStyles({
  formular: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    maxWidth: '32rem',
  },
});

const INVALID_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Name wurde nicht gespeichert.',
  explanation: 'Eine Angabe passt noch nicht.',
  nextStep: 'Korrigiere das markierte Feld und speichere erneut.',
  retryable: false,
});

const UNVERAENDERT = 'Der Name war bereits so gespeichert.';

export function NameAbschnitt({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  party,
  onParty,
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly party: PartyDetail & { readonly person: NonNullable<PartyDetail['person']> };
  readonly onParty: (party: PartyDetail) => void;
}): ReactElement {
  const own = useStyles();
  const [vorname, setVorname] = useState(party.person.givenName ?? '');
  const [nachname, setNachname] = useState(party.person.familyName ?? '');

  const zeigen = (next: PartyDetail): void => {
    onParty(next);
    setVorname(next.person?.givenName ?? '');
    setNachname(next.person?.familyName ?? '');
  };

  const befehle = useSectionCommands({
    accessToken,
    onReauthenticate,
    keepsInputOnConflict: true,
    // Nur nach Konflikt oder 404: den jetzt gueltigen Namen zeigen, ohne die
    // Seite auszuhaengen. Scheitert der Abruf, meldet der Hook das.
    refresh: async () => {
      const token = await accessToken();
      if (token === undefined) {
        // Wie beim stillen Laden der Pflegeabschnitte: ohne Sitzung kein Stand, also anmelden.
        onReauthenticate();
        return;
      }
      const result = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId: party.partyId });
      if (result.outcome === 'loaded') {
        zeigen(result.party);
        return undefined;
      }
      return result.outcome === 'rejected' ? result.message : PERSON_NAME_MESSAGES.notFound;
    },
  });
  const { message, messageOffen, confirmation, fieldErrors: fehler, busy: sendet, statusRef } = befehle;

  const speichern = (event: FormEvent): void => {
    event.preventDefault();
    if (sendet) {
      return;
    }
    const vorher = party.person.lockVersion;
    void befehle.command({
      run: (token) =>
        renamePerson({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId: party.partyId,
          givenName: vorname,
          familyName: nachname,
          lockVersion: vorher,
        }),
      confirm: (applied) => (applied.lockVersion === vorher ? UNVERAENDERT : personNameConfirmation(applied)),
      // Die Antwort traegt den gespeicherten Namen; ein zweiter Abruf entfaellt.
      reload: false,
      onApplied: (applied) =>
        zeigen({
          ...party,
          displayName: applied.displayName,
          person: {
            ...(applied.givenName === undefined ? {} : { givenName: applied.givenName }),
            ...(applied.familyName === undefined ? {} : { familyName: applied.familyName }),
            lockVersion: applied.lockVersion,
          },
        }),
    });
  };

  const fehlerAm = (fieldPath: string): string | null => {
    const treffer = fehler.find((eintrag) => eintrag.fieldPath === fieldPath);
    return treffer === undefined ? null : personNameValidationMessage(fieldPath, treffer.reasonCode);
  };
  const vornameFehler = fehlerAm('person.givenName');
  const nachnameFehler = fehlerAm('person.familyName');
  const problem = fehler.length > 0 ? INVALID_MESSAGE : message;

  // Ein Stand, den der Server nie fuehrt: vor der Eingabe sagen, statt sie nach dem Tippen abzuweisen.
  if (party.person.lockVersion < 1) {
    return (
      <div role="status" aria-label="Status: Name">
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>{UNKNOWN_LOCK_VERSION_MESSAGE.headline}</MessageBarTitle>
            <div>{UNKNOWN_LOCK_VERSION_MESSAGE.explanation}</div>
            <div>{UNKNOWN_LOCK_VERSION_MESSAGE.nextStep}</div>
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <form className={own.formular} onSubmit={speichern} noValidate>
      <Body1>In Listen und in der Suche erscheint der Kontakt als „Vorname Nachname“. Ein Feld darf leer bleiben.</Body1>
      <Field label="Vorname" validationState={vornameFehler === null ? 'none' : 'error'} validationMessage={vornameFehler}>
        <Input value={vorname} onChange={(_, data) => setVorname(data.value)} autoComplete="off" />
      </Field>
      <Field label="Nachname" validationState={nachnameFehler === null ? 'none' : 'error'} validationMessage={nachnameFehler}>
        <Input value={nachname} onChange={(_, data) => setNachname(data.value)} autoComplete="off" />
      </Field>
      <div ref={statusRef} tabIndex={-1} role="status" aria-live="polite" aria-label="Status: Name">
        {confirmation !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>{confirmation === UNVERAENDERT ? 'Nichts geändert' : 'Gespeichert'}</MessageBarTitle>
              <div>{confirmation}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {problem !== undefined ? (
          <MessageBar intent={fehler.length > 0 ? 'error' : meldungsIntent(problem, messageOffen)}>
            <MessageBarBody>
              <MessageBarTitle>{problem.headline}</MessageBarTitle>
              <div>{problem.explanation}</div>
              <div>{problem.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>
      <div>
        <Button type="submit" disabled={sendet}>
          {sendet ? 'Wird gespeichert …' : 'Namen speichern'}
        </Button>
      </div>
    </form>
  );
}
